/* StockHub — ADHD-safe fund/portfolio tracker + learning hub.
 * Focus: index funds, not day-trading. NO live prices in Phase 1.
 * ADHD guardian: warns user gently at 3+ opens per day. */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  TrendingUp, BookOpen, Eye, Plus, Trash2, X, Coins,
  Sparkles, AlertCircle, Wind,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function LessonsView({ lessons }) {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <div className="space-y-2" data-testid="stocks-lessons">
      {lessons.map((l, i) => {
        const isOpen = openIdx === i;
        return (
          <section key={l.id} className="neo-card bg-white overflow-hidden" data-testid={`stocks-lesson-${l.id}`}>
            <button
              onClick={() => setOpenIdx(isOpen ? null : i)}
              className="w-full p-3 flex items-center gap-3 text-left hover:bg-amber-50"
              data-testid={`stocks-lesson-toggle-${l.id}`}
            >
              <div className="text-2xl flex-shrink-0">{l.emoji}</div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm">
                  <span className="opacity-50 mr-2">Lektion {l.n}</span>{l.title}
                </div>
                <div className="text-[10px] opacity-60">📖 {l.read_time_min} min läsning</div>
              </div>
              <span className="text-xs opacity-60">{isOpen ? '▲' : '▼'}</span>
            </button>
            {isOpen && (
              <div className="px-4 pb-4 space-y-3">
                <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-line">{l.content}</p>
                <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-2.5 text-xs font-bold text-amber-900">
                  {l.key_takeaway}
                </div>
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

function PortfolioView() {
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ fund_name: '', ticker: '', shares: '', avg_price: '', purchase_date: '', current_value: '', notes: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/stocks/portfolio`, { withCredentials: true });
      setData(r.data);
    } catch { toast.error('Kunde inte ladda portfölj'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/stocks/portfolio`, {
        fund_name: form.fund_name.trim(),
        ticker: form.ticker.trim(),
        shares: parseFloat(form.shares),
        avg_price: parseFloat(form.avg_price),
        purchase_date: form.purchase_date || '',
        current_value: form.current_value ? parseFloat(form.current_value) : null,
        notes: form.notes.trim(),
      }, { withCredentials: true });
      toast.success('💚 Innehav sparat');
      setShowForm(false);
      setForm({ fund_name: '', ticker: '', shares: '', avg_price: '', purchase_date: '', current_value: '', notes: '' });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kunde inte spara');
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Ta bort detta innehav?')) return;
    try {
      await axios.delete(`${API}/stocks/portfolio/${id}`, { withCredentials: true });
      load();
    } catch { toast.error('Kunde inte ta bort'); }
  };

  if (!data) return <div className="text-center py-6 opacity-50 text-xs">Laddar...</div>;

  const gainPositive = data.total_gain_sek >= 0;
  const hasValue = data.total_value > 0;

  return (
    <div className="space-y-3" data-testid="stocks-portfolio">
      {/* Overview card */}
      <section className="neo-card p-4 bg-gradient-to-br from-[#FFF7E6] via-[#FFF0F6] to-[#E8F0FF]">
        <div className="text-[10px] font-bold uppercase tracking-widest opacity-60">Portföljöversikt</div>
        <div className="mt-1 grid grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] opacity-60">Anskaffning</div>
            <div className="text-xl font-bold font-['Fredoka']" data-testid="stocks-total-cost">
              {data.total_cost.toLocaleString('sv-SE')} kr
            </div>
          </div>
          <div>
            <div className="text-[10px] opacity-60">Nuvarande värde</div>
            <div className="text-xl font-bold font-['Fredoka']" data-testid="stocks-total-value">
              {hasValue ? `${data.total_value.toLocaleString('sv-SE')} kr` : '—'}
            </div>
          </div>
        </div>
        {hasValue && (
          <div className={`mt-3 p-2 rounded-xl text-center text-sm font-bold ${gainPositive ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-50 text-rose-800'}`}>
            {gainPositive ? '📈' : '📉'} {gainPositive ? '+' : ''}{data.total_gain_sek.toLocaleString('sv-SE')} kr ({gainPositive ? '+' : ''}{data.total_gain_pct}%)
          </div>
        )}
        <p className="text-[10px] italic opacity-60 mt-2">
          💡 Ange nuvarande värde manuellt (från Avanza/Nordnet) för att se din avkastning. Uppdatera max 1 gång/vecka.
        </p>
      </section>

      {/* Add button */}
      <button
        onClick={() => setShowForm(!showForm)}
        className="neo-btn w-full py-2.5 bg-[#B9FBC0] font-bold flex items-center justify-center gap-2"
        data-testid="stocks-portfolio-add-toggle"
      >
        <Plus className="w-4 h-4" /> {showForm ? 'Avbryt' : 'Lägg till innehav'}
      </button>

      {showForm && (
        <form onSubmit={submit} className="neo-card p-3 bg-white space-y-2" data-testid="stocks-portfolio-form">
          <input required placeholder="Fondnamn (t.ex. SPP Global)" value={form.fund_name} onChange={(e) => setForm({ ...form, fund_name: e.target.value })} className="neo-input w-full text-sm" data-testid="stocks-form-name" />
          <div className="grid grid-cols-2 gap-2">
            <input required type="number" step="0.0001" placeholder="Antal andelar" value={form.shares} onChange={(e) => setForm({ ...form, shares: e.target.value })} className="neo-input text-sm" data-testid="stocks-form-shares" />
            <input required type="number" step="0.01" placeholder="Snittkurs (kr)" value={form.avg_price} onChange={(e) => setForm({ ...form, avg_price: e.target.value })} className="neo-input text-sm" data-testid="stocks-form-price" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} className="neo-input text-sm" />
            <input type="number" step="0.01" placeholder="Nuvarande värde (frivilligt)" value={form.current_value} onChange={(e) => setForm({ ...form, current_value: e.target.value })} className="neo-input text-sm" data-testid="stocks-form-current" />
          </div>
          <input placeholder="Anteckningar (frivilligt)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="neo-input w-full text-sm" />
          <button type="submit" disabled={busy} className="neo-btn w-full py-2 bg-emerald-400 font-bold text-sm" data-testid="stocks-form-submit">
            {busy ? 'Sparar...' : '💚 Spara innehav'}
          </button>
        </form>
      )}

      {/* Holdings list */}
      {data.items.length === 0 && (
        <p className="text-center text-xs opacity-50 italic py-6">
          Inga innehav än. Kolla på fliken 🎓 Lär dig först — sen lägger du till efter du satt upp ISK 🌱
        </p>
      )}
      {data.items.map((h) => (
        <div key={h.id} className="neo-card p-3 bg-white flex items-start gap-3" data-testid={`stocks-holding-${h.id}`}>
          <Coins className="w-5 h-5 flex-shrink-0 text-amber-500 mt-1" />
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm">{h.fund_name}</div>
            <div className="text-[11px] opacity-70">
              {h.shares} andelar · snittkurs {h.avg_price} kr · anskaffning {h.cost_basis?.toLocaleString('sv-SE')} kr
            </div>
            {h.current_value !== null && h.current_value !== undefined && (
              <div className={`text-xs mt-1 font-bold ${(h.gain_sek || 0) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                Nu: {h.current_value.toLocaleString('sv-SE')} kr · {(h.gain_sek || 0) >= 0 ? '+' : ''}{h.gain_sek?.toLocaleString('sv-SE')} kr ({(h.gain_pct || 0) >= 0 ? '+' : ''}{h.gain_pct}%)
              </div>
            )}
            {h.notes && <div className="text-[10px] italic opacity-60 mt-0.5">{h.notes}</div>}
          </div>
          <button onClick={() => del(h.id)} className="opacity-40 hover:opacity-100 p-1" data-testid={`stocks-holding-del-${h.id}`}>
            <Trash2 className="w-4 h-4 text-red-600" />
          </button>
        </div>
      ))}
    </div>
  );
}

function WatchlistView() {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/stocks/watchlist`, { withCredentials: true });
      setData(r.data);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-center py-6 opacity-50 text-xs">Laddar...</div>;

  return (
    <div className="space-y-3" data-testid="stocks-watchlist">
      <p className="text-[10px] font-bold uppercase tracking-widest opacity-60">✨ Curerade förslag för ADHD-hjärnor</p>
      {data.suggestions.map((s) => (
        <div
          key={s.ticker}
          className="neo-card p-3 flex items-start gap-3"
          style={{ backgroundColor: `${s.color}66` }}
          data-testid={`stocks-watch-suggest-${s.ticker}`}
        >
          <Eye className="w-5 h-5 flex-shrink-0 mt-1 opacity-70" />
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-2 flex-wrap">
              <span className="font-bold text-sm">{s.name}</span>
              <span className="text-[10px] bg-white px-2 py-0.5 rounded-full border border-black/20 font-bold">
                Avgift: {s.avgift}
              </span>
            </div>
            <div className="text-[11px] opacity-70 mt-0.5">{s.type} · Risk: {s.risk}</div>
            <p className="text-xs mt-1 leading-relaxed">{s.description}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function AdhdGuardianBanner({ count, onBreath }) {
  if (count < 3) return null;
  return (
    <div className="neo-card p-3 bg-gradient-to-r from-amber-100 to-rose-100 border-2 border-amber-400 flex items-start gap-2" data-testid="stocks-guardian-banner">
      <AlertCircle className="w-5 h-5 flex-shrink-0 text-amber-700 mt-0.5" />
      <div className="flex-1 text-xs text-amber-900 leading-snug">
        <b>Du har öppnat aktier-fliken {count} gånger idag.</b> 🐾 Priset ändras inte i praktiken.
        Din hjärna letar dopamin — ge den ett djupt andetag istället. Portföljen tackar dig om 20 år för att du inte pillade idag. 💚
      </div>
      <button onClick={onBreath} className="neo-btn px-2 py-1 bg-white text-xs font-bold" data-testid="stocks-guardian-breathe">
        <Wind className="w-3 h-3 inline mr-1" /> Andas 4-4-8
      </button>
    </div>
  );
}

function BreathModal({ onClose }) {
  const [step, setStep] = useState(0); // 0=in, 1=hold, 2=out
  const [count, setCount] = useState(4);
  const [round, setRound] = useState(0);
  const phaseTime = [4, 4, 8];
  const phaseLabel = ['Andas in långsamt...', 'Håll...', 'Andas ut...'];

  useEffect(() => {
    if (round >= 3) return undefined;
    const id = setTimeout(() => {
      if (count > 1) setCount(count - 1);
      else {
        const nextStep = (step + 1) % 3;
        setStep(nextStep);
        setCount(phaseTime[nextStep]);
        if (nextStep === 0) setRound((r) => r + 1);
      }
    }, 1000);
    return () => clearTimeout(id);
  }, [count, step, round]);

  const done = round >= 3;

  return (
    <div className="fixed inset-0 bg-black/70 z-[110] flex items-center justify-center p-4" onClick={onClose} data-testid="stocks-breath-modal">
      <div className="bg-gradient-to-br from-[#1a0e3a] via-[#3d1e6b] to-[#5a2b8f] text-white rounded-3xl max-w-sm w-full p-8 relative text-center shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 p-1 rounded-full bg-white/20 hover:bg-white/30" aria-label="Stäng"><X className="w-5 h-5" /></button>
        {!done ? (
          <>
            <div className="text-xs opacity-60 uppercase tracking-widest">Runda {round + 1} / 3</div>
            <div className="mt-3 text-lg font-['Fredoka']">{phaseLabel[step]}</div>
            <div className="my-8">
              <div className={`w-40 h-40 rounded-full mx-auto transition-all duration-1000 flex items-center justify-center text-5xl font-bold tabular-nums ${step === 0 ? 'bg-blue-400 scale-100' : step === 1 ? 'bg-purple-400 scale-100' : 'bg-pink-400 scale-75'}`}>
                {count}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="text-4xl">🌬️✨</div>
            <p className="text-sm mt-3 opacity-90">Klart! Din portfölj är i gott sällskap med universum. 💫</p>
            <button onClick={onClose} className="mt-4 neo-btn px-6 py-2 bg-white/20 hover:bg-white/30 font-bold">Klar</button>
          </>
        )}
      </div>
    </div>
  );
}

export default function StockHub() {
  const [sub, setSub] = useState('learn');
  const [lessons, setLessons] = useState([]);
  const [guardian, setGuardian] = useState({ count: 0, threshold_warn: 3 });
  const [breathOpen, setBreathOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [lr, gr] = await Promise.all([
          axios.get(`${API}/stocks/learning`, { withCredentials: true }),
          axios.post(`${API}/stocks/guardian/open`, {}, { withCredentials: true }),
        ]);
        setLessons(lr.data.lessons || []);
        setGuardian(gr.data);
      } catch { /* silent */ }
    })();
  }, []);

  return (
    <div className="space-y-4" data-testid="stocks-hub">
      <header className="neo-card p-4 bg-gradient-to-br from-[#FFE8A3] via-[#FFF7E6] to-[#B9FBC0]">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-6 h-6" />
          <div>
            <h2 className="text-lg font-bold font-['Fredoka']">Aktier & Fonder</h2>
            <p className="text-[11px] opacity-70 italic">ADHD-vänligt: inga live-priser, ingen dopamin-fälla. Bara långsiktig visdom. 🌱</p>
          </div>
        </div>
      </header>

      <AdhdGuardianBanner count={guardian.count} onBreath={() => setBreathOpen(true)} />

      <div className="flex gap-1.5 flex-wrap" data-testid="stocks-subtabs">
        {[
          { id: 'learn',    label: 'Lär dig',    emoji: '📚', icon: BookOpen },
          { id: 'portfolio', label: 'Portfölj',  emoji: '💼', icon: Coins },
          { id: 'watch',    label: 'Bevakning',  emoji: '👀', icon: Eye },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setSub(t.id)}
            className={`neo-btn px-3 py-2 text-sm font-bold flex items-center gap-1 ${sub === t.id ? 'bg-[#FFE8A3]' : 'bg-white'}`}
            data-testid={`stocks-subtab-${t.id}`}
          >
            <span>{t.emoji}</span> {t.label}
          </button>
        ))}
      </div>

      {sub === 'learn' && lessons.length > 0 && <LessonsView lessons={lessons} />}
      {sub === 'portfolio' && <PortfolioView />}
      {sub === 'watch' && <WatchlistView />}

      <div className="text-center pt-4">
        <p className="text-[10px] italic opacity-40 leading-relaxed max-w-md mx-auto">
          <Sparkles className="w-3 h-3 inline mr-1" />
          Detta är UTBILDNING, inte finansiell rådgivning. All investering innebär risk.
          Passiv månadssparande i indexfonder är historiskt den mest ADHD-vänliga strategin. 💚
        </p>
      </div>

      {breathOpen && <BreathModal onClose={() => setBreathOpen(false)} />}
    </div>
  );
}
